import { useCallback, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Eye, EyeOff, GraduationCap, Pencil, Plus, Sparkles, Tags, Trash2, Wand2 } from 'lucide-react';
import type { MailCategory } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { useCategories } from '../../hooks/useCategories';
import { useCurrentWorkspace } from '../../hooks/useWorkspace';
import { Badge, Button, Card, CardBody, CardDivider, CardHeader, EmptyState, ErrorState, Field, InlineError, Input, Modal, Skeleton, Textarea, useConfirm, useToast } from '../../components/ui';
import { Rows, SettingRow, StatusPill, TipIconButton } from '../../components/settings/SettingsPrimitives';
import { SettingsTitle } from './SettingsLayout';

const COLORS = ['#0EA5E9', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#E11D48', '#6366F1', '#F97316', '#64748B'];

function CategoryModal({ open, onClose, editing }: { open: boolean; onClose: () => void; editing: MailCategory | null }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [color, setColor] = useState(editing?.color ?? COLORS[7]);
  const save = useMutation({
    mutationFn: () =>
      editing
        ? api('PATCH /api/categories/:id', { params: { id: editing.id }, body: { name: name.trim(), description: description.trim(), color } })
        : api('POST /api/categories', { body: { name: name.trim(), description: description.trim(), color } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: qk.categories });
      toast.success(editing ? `Saved “${r.category.name}”` : `Added “${r.category.name}”. New mail can be sorted into it from now on.`);
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) save.mutate();
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.name}` : 'New category'}
      description="The description is what the AI reads to decide what belongs here — be specific."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="category-form" loading={save.isPending} disabled={!name.trim()}>
            {editing ? 'Save' : 'Add category'}
          </Button>
        </>
      }
    >
      <form id="category-form" onSubmit={submit} className="space-y-5">
        <Field label="Name" required>
          {(id) => <Input id={id} data-autofocus value={name} maxLength={40} onChange={(e) => setName(e.target.value)} placeholder="Key accounts" />}
        </Field>
        <Field label="What belongs here" hint="e.g. “Anything from Acme, Globex or Initech — our largest clients.”">
          {(id) => <Textarea id={id} rows={3} value={description} maxLength={400} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
        <div className="space-y-2">
          <p className="text-[0.8125rem] font-medium text-ink">Colour</p>
          <div role="radiogroup" aria-label="Colour" className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={color === c}
                aria-label={c}
                onClick={() => setColor(c)}
                className={clsx('h-8 w-8 rounded-full transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2', color === c && 'ring-2 ring-offset-2 ring-offset-surface')}
                style={{ background: c, ['--tw-ring-color' as string]: c }}
              />
            ))}
          </div>
        </div>
        <InlineError error={save.error} />
      </form>
    </Modal>
  );
}

export function CategoriesSettings() {
  const cats = useCategories();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { isAdmin } = useCurrentWorkspace();
  const [modal, setModal] = useState<{ open: boolean; editing: MailCategory | null; n: number }>({ open: false, editing: null, n: 0 });
  const closeModal = useCallback(() => setModal((m) => ({ ...m, open: false })), []);

  const patch = useMutation({
    mutationFn: ({ id, hidden }: { id: string; hidden: boolean }) => api('PATCH /api/categories/:id', { params: { id }, body: { hidden } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.categories }),
    onError: (e) => toast.error(e),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE /api/categories/:id', { params: { id } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.categories });
      void qc.invalidateQueries({ queryKey: qk.threadsRoot });
    },
    onError: (e) => toast.error(e),
  });
  const resort = useMutation({
    mutationFn: (scope: 'uncategorized' | 'recent') => api('POST /api/categories/recategorize', { body: { scope } }),
    onSuccess: (r) =>
      r.queued === 0
        ? toast.info('Nothing to sort right now.')
        : r.usingAi
          ? toast.success(`Sorting ${r.queued} conversation${r.queued === 1 ? '' : 's'} with AI. They update as they're done.`)
          : toast.info(`Sorting ${r.queued} conversations without AI (${r.reason ?? 'AI is not set up'}). Set up an AI provider for better results.`, { label: 'AI settings', to: '/settings/ai' }),
    onError: (e) => toast.error(e),
  });

  const title = <SettingsTitle title="Categories" description="MailPilot sorts every conversation into a category so you can find things fast. The AI decides for new mail; when you move a conversation yourself, it remembers and sorts that sender the same way next time." />;
  if (cats.isPending) {
    return (
      <div>
        {title}
        <div className="space-y-6" aria-busy="true">
          <Skeleton className="h-48 w-full rounded-panel" />
          <Skeleton className="h-96 w-full rounded-panel" />
        </div>
      </div>
    );
  }
  if (cats.error || !cats.data) {
    return (
      <div>
        {title}
        <Card>
          <ErrorState error={cats.error} onRetry={() => void cats.refetch()} />
        </Card>
      </div>
    );
  }
  const list = cats.data.categories;
  const uncategorized = cats.data.uncategorized;

  return (
    <div>
      {title}
      <div className="space-y-6">
        <Card>
          <CardHeader
            icon={<Wand2 className="h-5 w-5" />}
            title="Sort with AI"
            description="Imported history is sorted by sender and keywords; the AI can refine it."
            actions={<StatusPill tone={uncategorized ? 'warn' : 'success'}>{uncategorized ? `${uncategorized} uncategorised` : 'Everything sorted'}</StatusPill>}
          />
          <CardDivider className="mt-5" />
          <CardBody className="!py-5">
            <Rows>
              <SettingRow
                label="Sort uncategorised mail"
                description={uncategorized ? `${uncategorized} inbox conversation${uncategorized === 1 ? '' : 's'} ${uncategorized === 1 ? 'has' : 'have'} no category yet.` : 'Every inbox conversation already has a category.'}
                control={
                  <Button variant="primary" size="sm" icon={<Sparkles className="h-4 w-4" />} disabled={!isAdmin || !uncategorized} loading={resort.isPending && resort.variables === 'uncategorized'} onClick={() => resort.mutate('uncategorized')}>
                    Sort now
                  </Button>
                }
              />
              <SettingRow
                label="Re-sort recent mail"
                description="The AI looks at up to 200 recent inbox conversations again. Conversations you sorted yourself are never changed."
                control={
                  <Button
                    size="sm"
                    icon={<Wand2 className="h-4 w-4" />}
                    disabled={!isAdmin}
                    loading={resort.isPending && resort.variables === 'recent'}
                    onClick={async () => {
                      const ok = await confirm({ title: 'Re-sort recent mail?', message: 'The AI looks at up to 200 recent inbox conversations again. Conversations you sorted yourself are never changed.', confirmLabel: 'Re-sort' });
                      if (ok) resort.mutate('recent');
                    }}
                  >
                    Re-sort
                  </Button>
                }
              />
              <SettingRow
                icon={<GraduationCap className="h-4 w-4" aria-hidden />}
                label="Teach it as you go"
                description="Open a conversation and change its category. MailPilot follows your choice for that sender, and after two choices the same way, for their whole company domain."
              />
            </Rows>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            icon={<Tags className="h-5 w-5" />}
            title="Your categories"
            description={isAdmin ? 'Hidden categories are never assigned. Custom categories are used by the AI only.' : "You can move conversations between categories. Adding or changing categories is up to your organization's admins."}
            actions={
              isAdmin && (
                <Button size="sm" variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setModal((m) => ({ open: true, editing: null, n: m.n + 1 }))}>
                  New category
                </Button>
              )
            }
          />
          {list.length === 0 ? (
            <EmptyState icon={<Tags className="h-6 w-6" />} title="No categories yet" description="Built-in categories appear after the first sync." />
          ) : (
            <>
              <CardDivider className="mt-5" />
              <ul className="divide-y divide-line/70">
                {list.map((c) => (
                  <li key={c.id} className={clsx('group flex items-center gap-3.5 px-6 py-3.5 transition-colors hover:bg-raised/40', c.hidden && 'opacity-60')}>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-sunken">
                      <span className="h-3 w-3 rotate-45 rounded-[3px]" style={{ background: c.color }} aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-2 font-medium text-ink">
                        {c.name}
                        {!c.builtin && <Badge tone="primary">Custom</Badge>}
                        {c.hidden && <Badge>Hidden</Badge>}
                      </p>
                      {c.description && <p className="mt-0.5 truncate text-xs text-muted">{c.description}</p>}
                    </div>
                    <span className="shrink-0 text-xs tabular-nums text-muted" title="Inbox conversations">
                      {c.total.toLocaleString()}
                      {c.unread ? <span className="ml-1 font-semibold text-primary-ink">· {c.unread} new</span> : null}
                    </span>
                    {isAdmin && (
                      <div className="flex shrink-0 items-center transition-opacity [@media(hover:hover)]:opacity-50 group-hover:opacity-100 group-focus-within:opacity-100">
                        <TipIconButton size="sm" label={c.hidden ? `Use ${c.name}` : `Hide ${c.name}`} icon={c.hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />} onClick={() => patch.mutate({ id: c.id, hidden: !c.hidden })} />
                        <TipIconButton size="sm" label={`Edit ${c.name}`} icon={<Pencil className="h-4 w-4" />} onClick={() => setModal((m) => ({ open: true, editing: c, n: m.n + 1 }))} />
                        {!c.builtin ? (
                          <TipIconButton
                            size="sm"
                            label={`Delete ${c.name}`}
                            tipPlacement="bottom-end"
                            icon={<Trash2 className="h-4 w-4" />}
                            className="hover:!bg-danger-soft hover:!text-danger-ink"
                            onClick={async () => {
                              const ok = await confirm({ title: `Delete “${c.name}”?`, message: `${c.total ? `${c.total} conversation${c.total === 1 ? '' : 's'} will become uncategorised. ` : ''}What MailPilot learned for this category is removed too.`, confirmLabel: 'Delete', danger: true });
                              if (ok) remove.mutate(c.id);
                            }}
                          />
                        ) : (
                          <span className="h-8 w-8" aria-hidden />
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>
      {/* keyed so each open starts from the right values */}
      <CategoryModal key={modal.n} open={modal.open} onClose={closeModal} editing={modal.editing} />
    </div>
  );
}
